import { Polygon, PolygonShape } from "../display/Polygon.js";
import { Stage } from "../display/Stage.js";
import { TabBar } from "../display/TabBar.js";
import { Data } from "../model/Data.js";
import { VOOrchestraLayout, VOOrchestraLayoutPlace } from "../model/DataStructure.js";

export interface LayoutAppOptions {
    element: HTMLDivElement;
    editable: boolean;
    restUrl: string;
}

export class LayoutApp {

    private _element: HTMLDivElement;
    private _stage: Stage;
    private _data: Data;
    private _options: LayoutAppOptions;
    private _items: LayoutAppItem[] = [];
    private _current: LayoutAppItem = null;

    constructor(options: LayoutAppOptions) {

        this._options = options;
        this._element = options.element;

        this.init();
    }

    private async init() {
        this._stage = new Stage({
            element: this._element,
            className: "layout-app",
            layout: "responsive"
        });

        try {
            
            this._data = new Data();

            await this._data.load(this._element.getAttribute("data-data"));

            const bar = new TabBar<VOOrchestraLayout>(
                {
                    container: this._stage.foreground,
                    direction: "horizontal",
                    onSelect: (data) => {
                        this.current = data;
                    }
                });

            let layouts: VOOrchestraLayout[] = [];
            layouts = this._data.data.phil_o_layout.slice();
            layouts = layouts.concat(this._data.data.phil_intro_layout);

            if (layouts.length > 1) {
                for (let layout of layouts) {
                    bar.add({
                        data: layout,
                        label: layout.title
                    });
                }
            }

            this._items = layouts.map((layout) => {
                return new LayoutAppItem({
                    layout: layout,
                    container: this._stage.content,
                    editable: this._options.editable,
                    onClick: (item) => {
                        this.current = item.layout;
                    }
                });
            });

            let layoutId = parseInt(e5.Url.params.get('layout', '0'));
            if (layoutId) {
                this.current = this._items.find((i) => i.layout.id == layoutId)?.layout;
            }

            if (!this.current) {
                this.current = this._items[0]?.layout;
            }

            bar.current = this.current;

            if (this._options.editable) {
                let submit: HTMLButtonElement = null;
                submit = document.createElement("button");
                submit.type = "button";
                submit.textContent = "Speichern";
                submit.className = "layout-app__submit";
                this._stage.foreground.appendChild(submit);

                submit.addEventListener("click", () => {
                    this.submit();
                });
            }

        } catch (error) {
            var err = error as Error;
            console.error(error);
            this._stage.preloader.showError(err?.message || "Es ist ein Unbekannter Fehler aufgetreten.");
            return;
        }

        this._stage.preloader.hide();
    }

    public get current(): VOOrchestraLayout {
        return this._current?.layout;
    }

    public set current(value: VOOrchestraLayout) {
        const item = this.get(value);
        if (this._current == item) {
            return;
        }

        if (this._current) {
            this._current.visible = false;
        }

        this._current = item;

        if (this._current) {
            this._current.visible = true;
        }
    }

    private get(value: VOOrchestraLayout): LayoutAppItem {
        for (let item of this._items) {
            if (item.layout.id === value.id) {
                return item;
            }
        }
        return null;
    }

    private _submitting: boolean = false;
    private async submit() {
        if (this._submitting) {
            return;
        }
        this._submitting = true;
        const response: any = await e5.load(
            "json",
            this._options.restUrl,
            {
                data: {
                    layout: {
                        layout_id: this._current.layout.id,
                        places: this._current.getPlaceData()
                    }
                }
            }
        );

        if (response?.kind === 'success') {
            console.log("RESPONSE", response);
            e5.Toast.showText("Layout wurde gespeichert!");
        } else {
            console.log("RESPONSE", response);
            e5.Toast.showText("Leider ist etwas schief gelaufen.");
        }

        this._submitting = false;
    }
}

interface LayoutAppItemOptions {
    layout: VOOrchestraLayout;
    container: HTMLElement;
    buttonGroup?: HTMLElement;
    editable?: boolean;
    onClick: (item: LayoutAppItem) => void;
}

class LayoutAppItem {

    public readonly layout: VOOrchestraLayout;

    private _element: HTMLDivElement;
    private _options: LayoutAppItemOptions;
    private _placeItems: PlaceItem[] = [];

    constructor(options: LayoutAppItemOptions) {

        this._options = e5.object.merge({
            editable: false
        }, options);

        this.layout = options.layout;

        this._element = document.createElement("div");
        this._element.className = "layout-app__item e5-hidden";


        this._placeItems = this.layout.places.map((place) => {
            return new PlaceItem({
                container: this._element,
                place: place,
                layout: this.layout,
                editable: options.editable
            });
        });

        options.container.appendChild(this._element);
    }

    public get visible(): boolean {

        return !this._element.classList.contains("e5-hidden");
    }

    public set visible(value: boolean) {
        this._element.classList.toggle("e5-hidden", !value);
    }

    public getPlaceData(): VOOrchestraLayoutPlace[] {
        let result: VOOrchestraLayoutPlace[] = [];
        for (var item of this._placeItems) {
            result.push(item.getPlaceData());
        }
        return result;
    }
}

interface PlaceItemOptions {
    layout: VOOrchestraLayout;
    place: VOOrchestraLayoutPlace;
    container: HTMLElement;
    editable?: boolean;
}

class PlaceItem {
    private _element: HTMLDivElement;
    private _options: PlaceItemOptions;
    private _polygon: Polygon;

    private _x: number = 0;
    private _y: number = 0;
    private _shape: PolygonShape = "1";

    constructor(options: PlaceItemOptions) {

        this._options = e5.object.merge({
            editable: false
        }, options);

        const place = options.place;

        this._element = document.createElement("div");
        this._element.className = "layout-app__item-place";
        if (this._options.editable) {
            this._element.classList.add("layout-app__item-place--editable");
        }
        this._element.classList.add("layout-app__item-place--kind-" + options.layout.kind);
        this._element.style.left = place.x + "px";
        this._element.style.top = place.y + "px";
        this._element.innerHTML = `
        <div class="layout-app__item-place-fill"></div>
        <div class="layout-app__item-place-position">${(place.index + 1)}</div>
        <div class="layout-app__item-place-shape">${(place.shape)}</div>
        `;

        this._polygon = new Polygon({
            shape: place.shape,
            // color: "#333",
            container: this._element,
            prepend: true
        });

        this._x = place.x;
        this._y = place.y;
        this._shape = place.shape;

        if (options.editable) {
            this.makeEditable();
        }

        options.container.appendChild(this._element);
    }

    private get shape(): PolygonShape {
        return this._shape;
    }

    private set shape(value: PolygonShape) {
        this._shape = value;
        this._polygon.shape = value;
        this._element.querySelector(".layout-app__item-place-shape").textContent = value;
    }

    public getPlaceData(): VOOrchestraLayoutPlace {
        return {
            shape: this._shape,
            x: this._x,
            y: this._y
        };
    }

    private makeEditable(): void {

        const p = this._element;

        const drag = new e5.Drag(p, {
            clamp: {
                parent: this._options.container,
                boundsParent: 0
            },
            useTransform: false,
            snap: {
                snapFunction: e5.SnapFunctions.grid(10, 10),
            },
            snapTrigger: e5.Trigger.ALL
        });

        drag.onStart.add(() => {
            this._element.classList.add("layout-app__item-place--dragging");
        });

        drag.onClear.add(() => {
            this._element.classList.remove("layout-app__item-place--dragging");
            let position = drag.position;
            this._x = Math.round(position.x);
            this._y = Math.round(position.y);
        });

        p.addEventListener("wheel", (e) => {
            e.preventDefault();
            let shapeNumber = parseInt(this._polygon.shape);
            if (e.deltaY > 0) {
                shapeNumber--;
            } else {
                shapeNumber++;
            }
            if (shapeNumber > Polygon.TOTAL_SHAPES) {
                shapeNumber = 1;
            } else if (shapeNumber < 1) {
                shapeNumber = Polygon.TOTAL_SHAPES;
            }
            this.shape = String(shapeNumber) as PolygonShape;
        }, {
            passive: false
        });
    }
}